import { useToast } from '@viquard/doc-ui';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ZodError, type ZodIssue } from 'zod';
import { ApiRequestError } from '@/lib/api';
import { logClientError } from '@/lib/client-error-log';

export interface AuthToastApi {
  /** Resolve a user-facing title for an auth-flow error without showing it. */
  errorTitle: (err: unknown) => string;
  /** Surface an unknown error / API failure as a danger toast. */
  notifyError: (err: unknown) => void;
  /** Generic positive notification (e.g. "code sent", "password updated"). */
  notifySuccess: (message: string, opts?: { description?: string }) => void;
  /** Neutral info — e.g. cooldown reminders. */
  notifyInfo: (message: string, opts?: { description?: string }) => void;
}

function issueCode(issue: ZodIssue): string | null {
  if (!('params' in issue)) return null;
  const params: unknown = issue.params;
  if (typeof params !== 'object' || params === null) return null;
  const code = (params as { code?: unknown }).code;
  return typeof code === 'string' ? code : null;
}

/** Funnel auth-flow errors into the global toast surface so the user sees
 *  one consistent affordance instead of a raw "errors.INTERNAL_ERROR"
 *  string under a form field.
 *
 *  i18n key resolution order:
 *    1. `auth:errors.<code>`             — the canonical, hand-written copy
 *    2. `auth:errors.UNKNOWN`            — graceful fallback for new codes
 *    3. (no raw API message ever shown)  — see below
 *
 *  Codes returned by the server include `AUTH_*` (auth-domain) and the
 *  HTTP envelope codes (`VALIDATION_ERROR`, `INTERNAL_ERROR`, …). We
 *  translate AUTH_* via the `auth.errors.*` table; everything else
 *  funnels through the same fallback chain.
 *
 *  Raw API messages are NEVER shown to users — they routinely contain
 *  internals (SQL fragments from Drizzle errors, stack-frame paths, etc.)
 *  that are confusing at best and a leakage risk at worst. The friendly
 *  i18n title alone is the user-facing copy; full detail still lands in
 *  the server logs and the browser console for engineers to triage. */
export function useAuthToast(): AuthToastApi {
  const toast = useToast();
  const { t, i18n } = useTranslation('auth');

  const resolveTitle = useCallback(
    (err: unknown): string => {
      if (err instanceof ApiRequestError) {
        const key = `errors.${err.code}`;
        if (i18n.exists(key, { ns: 'auth' })) return t(key);
        if (err.code === 'VALIDATION_ERROR') return t('errors.VALIDATION_ERROR');
        return t('errors.UNKNOWN');
      }
      if (err instanceof ZodError) {
        const code = err.issues.map(issueCode).find((item): item is string => item !== null);
        if (code !== undefined) {
          const key = `errors.${code}`;
          if (i18n.exists(key, { ns: 'auth' })) return t(key);
        }
        return t('errors.VALIDATION_ERROR');
      }
      return t('errors.UNKNOWN');
    },
    [t, i18n],
  );

  const notifyError = useCallback(
    (err: unknown) => {
      // Engineers still get the raw error in devtools — only the user-facing
      // toast is sanitised. This keeps prod incidents debuggable without
      // showing SQL / stack frames in the UI.
      logClientError('auth', err);
      toast.danger({
        title: resolveTitle(err),
        duration: 6000,
      });
    },
    [toast, resolveTitle],
  );

  const notifySuccess = useCallback(
    (message: string, opts: { description?: string } = {}) => {
      toast.success({
        title: message,
        ...(opts.description !== undefined ? { description: opts.description } : {}),
        duration: 4000,
      });
    },
    [toast],
  );

  const notifyInfo = useCallback(
    (message: string, opts: { description?: string } = {}) => {
      toast.info({
        title: message,
        ...(opts.description !== undefined ? { description: opts.description } : {}),
        duration: 4000,
      });
    },
    [toast],
  );

  return { errorTitle: resolveTitle, notifyError, notifySuccess, notifyInfo };
}
